import { Person } from "../types/person";
import { EntityController } from "./entityController";
import { useNavigate } from "react-router-dom";
import usePersonQuery from "../hooks/query/usePersonQuery";
import usePersonLogic from "../hooks/logic/personLogic";
import { extractReferenceKey } from "../components/forms/fields/basic/EntitySelectionField";

/**
 * Extended controller interface for Person with additional helper methods.
 * Extends the unified EntityController interface.
 */
export interface PersonController extends EntityController<Person> {
  /** The raw display name without "Edit" or "Create New" prefix */
  displayName: (data: Person) => string;
  /** Title formatted for view pages (just the display name) */
  viewTitle: (data: Person) => string;
  /** Title formatted for edit pages (includes "Edit" or "Create New" prefix) */
  editTitle: (data: Person) => string;
}

/**
 * Combined controller hook for Person entity.
 * Contains all logic needed for both view and edit pages.
 *
 * View pages use: fetchData, viewTitle, userEnums, getEditRoute
 * Edit pages use: fetchData, editTitle, userEnums, onSubmit, onDelete, defaultValues, etc.
 *
 * The unused properties (e.g., mutations on view pages) are initialized but never called.
 */
const usePersonController = (id: string | null): PersonController => {
  const { useFetchById, useUpdate, useCreate, useDeleteById } =
    usePersonQuery();
  const { defaultPerson, userEnums, getDisplayName, metadataFieldNames } =
    usePersonLogic();
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Person> => {
    if (!id) {
      return defaultPerson;
    }
    const res = await fetchById.refetch();
    const personData = res.data;
    if (!personData || res.isError) {
      throw new Error("Person data not found");
    }
    return personData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Person) => getDisplayName(data);

  const viewTitle = (data: Person) => displayName(data);

  const editTitle = (data: Person) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  const onSubmit = async (data: Partial<Person>) => {
    const updateData: Partial<Person> = {
      internal_notes: data.internal_notes,
      notes: data.notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      affiliated_traditions: data.affiliated_traditions,
      place_of_birth_id: extractReferenceKey(data.place_of_birth),
      date_of_birth: data.date_of_birth,
      date_of_death: data.date_of_death,
      floruit: data.floruit,
      region_of_activity_id: extractReferenceKey(data.region_of_activity),
      gender: data.gender,
      name_internal_evidence: data.name_internal_evidence,
      other_designations: data.other_designations,
      primary_title_type: data.primary_title_type,
      link_to_other_databases: data.link_to_other_databases,
      languages: data.languages,
      attributes_metadata: {
        ...defaultPerson.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      updateData.origin = data.origin;
    }

    let result: Person | null;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Person");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Person");
      }
    }

    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Person ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      throw new Error("Failed to delete person");
    }
  };

  const navigateBack = () => navigate(-1);

  const deleteWarningMessage = "Are you sure you want to delete this person?";

  const getEditRoute = () => `/persons/${id}/edit`;

  return {
    fetchData,
    userEnums,

    displayName,
    viewTitle,
    editTitle,
    title: viewTitle,

    defaultValues: defaultPerson,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    getEditRoute,
  };
};

export default usePersonController;
